
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, DOCUMENT } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';

import { OidcSettings } from '../models/oidc-settings.model';

interface VersionResponse {
  Version?: string;
  version?: string;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly settings$ = this.http
    .get<OidcSettings>(`${this.getApiBaseUrl()}/apiproxy/v1/settings/settings`, {
      headers: new HttpHeaders({
        'X-From-Angular': 'project-settings'
      })
    })
    .pipe(
      map((settings) => this.normalizeSettings(settings)),
      shareReplay(1)
    );
  private readonly proxyVersion$ = this.http
    .get<VersionResponse>(`${this.getApiBaseUrl()}/apiproxy/v1/Info/version`)
    .pipe(
      map((response) => response.Version ?? response.version ?? ''),
      shareReplay(1)
    );

  getOidcSettings(): Observable<OidcSettings> {
    return this.settings$;
  }

  getProxyVersion(): Observable<string> {
    return this.proxyVersion$;
  }

  getApiBaseUrl(): string {
    // Runtime mapping: localhost/127.0.0.1 uses GOEvProxy on :5000 (DevContainer and local Development);
    // deployed Development/PRE/PRO use the current site origin so the SPA calls the co-hosted proxy.
    const location = this.document.location;
    if (!location) {
      return '';
    }

    if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
      if (location.port === '5000') {
        return location.origin;
      }

      return `http://${location.hostname}:5000`;
    }

    return location.origin;
  }

  private normalizeSettings(settings: OidcSettings): OidcSettings {
    const origin = this.document.location?.origin ?? '';
    const loginUrl = origin ? `${origin}/login` : settings.redirectUri;

    const normalizedSettings = {
      ...settings,
      redirectUri: this.useConfiguredUri(settings.redirectUri, origin) ? settings.redirectUri : loginUrl,
      postLogoutRedirectUri: this.useConfiguredUri(settings.postLogoutRedirectUri, origin)
        ? settings.postLogoutRedirectUri
        : loginUrl,
      responseType: settings.responseType || 'code'
    };

    this.updateDocumentTitle(normalizedSettings.environmentName);

    return normalizedSettings;
  }

  private updateDocumentTitle(environmentName: string): void {
    const normalizedEnvironment = (environmentName || '').trim();
    const baseTitle = 'GO Evolution';

    if (!normalizedEnvironment
      || normalizedEnvironment.localeCompare('PRO', undefined, { sensitivity: 'accent' }) === 0
      || normalizedEnvironment.localeCompare('Production', undefined, { sensitivity: 'accent' }) === 0
      || normalizedEnvironment.localeCompare('PRD', undefined, { sensitivity: 'accent' }) === 0) {
      this.document.title = baseTitle;
      return;
    }

    this.document.title = `${baseTitle} - ${normalizedEnvironment}`;
  }

  private useConfiguredUri(uri: string, origin: string): boolean {
    if (!uri || !origin) {
      return false;
    }

    try {
      return new URL(uri).origin === origin;
    } catch {
      return false;
    }
  }
}